import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { Mail, Check, X as XIcon, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STATUS_TONE = {
  pending: "bg-[#C6D947]/30 text-[#0F1B47] border-[#C6D947]/60",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  declined: "bg-red-50 text-red-700 border-red-200",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function LeaveRequestsPanel({ can }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const editable = can("manage_attendance");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/leave-requests");
      setList(data || []);
    } catch {/* 401 handled globally */}
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (filter === "all") return list;
    return list.filter((r) => r.status === filter);
  }, [list, filter]);

  const counts = useMemo(() => ({
    all: list.length,
    pending: list.filter((r) => r.status === "pending").length,
    approved: list.filter((r) => r.status === "approved").length,
    declined: list.filter((r) => r.status === "declined").length,
  }), [list]);

  return (
    <div className="bg-[#FDFDF9] rounded-3xl border border-[#0F1B47]/8 overflow-hidden" data-testid="leave-requests-panel">
      <div className="p-6 md:p-7 border-b border-[#0F1B47]/8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#648DE5]/15 text-[#0F1B47] flex items-center justify-center">
            <MessageSquare size={18} />
          </div>
          <div>
            <h2 className="font-display text-2xl text-[#0F1B47] font-medium">Parent messages</h2>
            <p className="text-sm text-[#4A5568] mt-0.5 max-w-2xl">Leave requests submitted by parents from the parent portal. Approve or decline — parents see your decision on their portal.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="leave-filter-pills">
          {[
            { id: "all", label: "All" },
            { id: "pending", label: "Pending" },
            { id: "approved", label: "Approved" },
            { id: "declined", label: "Declined" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] font-bold tracking-wide uppercase px-3 py-1.5 rounded-full ${
                filter === f.id
                  ? "bg-[#0F1B47] text-[#FDFDF9]"
                  : "bg-[#0F1B47]/5 text-[#0F1B47] hover:bg-[#0F1B47]/10"
              }`}
              data-testid={`leave-filter-${f.id}`}
            >
              {f.label} · {counts[f.id] || 0}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6 md:p-7">
        {loading ? (
          <div className="text-sm text-[#718096] py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#F5F5F0] rounded-2xl p-10 text-center">
            <MessageSquare size={28} className="mx-auto text-[#648DE5] mb-2" />
            <div className="text-[#0F1B47] font-medium">No leave requests {filter !== "all" ? `with status "${filter}"` : "yet"}</div>
            <p className="text-xs text-[#718096] mt-1">Parents can submit leave requests from their portal.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="leave-requests-list">
            {filtered.map((r) => (
              <li
                key={r.id}
                onClick={() => setSelected(r)}
                className="bg-[#F5F5F0] rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 cursor-pointer hover:bg-[#0F1B47]/5 transition-colors"
                data-testid={`leave-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-[#0F1B47]">{r.student_name}</div>
                    <span className="text-[10px] font-medium text-[#4A5568]">{r.student_class} · {r.admission_id}</span>
                    <span className={`text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 border ${STATUS_TONE[r.status] || STATUS_TONE.pending}`}>
                      {r.status}
                    </span>
                  </div>
                  <div className="text-xs text-[#4A5568] mt-1.5">
                    <strong className="text-[#0F1B47]">{r.days} day{r.days === 1 ? "" : "s"}</strong> from {fmtDate(r.start_date)} · requested by {r.parent_name || r.parent_email}
                  </div>
                  <div className="text-[12px] text-[#0F1B47] mt-1 line-clamp-2">"{r.reason}"</div>
                  <div className="text-[10px] text-[#718096] mt-1.5">
                    <Clock size={10} className="inline mr-1" /> submitted {fmtDateTime(r.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected && (
        <LeaveDetailDrawer
          row={selected}
          editable={editable}
          onClose={() => setSelected(null)}
          onSaved={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}

function LeaveDetailDrawer({ row, editable, onClose, onSaved }) {
  const [adminNote, setAdminNote] = useState(row.admin_note || "");
  const [saving, setSaving] = useState(null);

  const decide = async (status) => {
    setSaving(status);
    try {
      await adminApi.patch(`/admin/leave-requests/${row.id}`, { status, admin_note: adminNote });
      toast.success(status === "approved" ? "Leave approved." : status === "declined" ? "Leave declined." : "Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setSaving(null); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F1B47]/60 flex items-end md:items-center justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FDFDF9] rounded-t-3xl md:rounded-3xl w-full md:max-w-md md:mr-8 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="leave-detail-drawer">
        <div className="px-6 py-5 border-b border-[#0F1B47]/8 flex items-start justify-between gap-3 sticky top-0 bg-[#FDFDF9] z-10">
          <div>
            <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#648DE5]">Leave request</div>
            <h3 className="font-display text-xl text-[#0F1B47] font-medium mt-0.5">{row.student_name}</h3>
            <p className="text-xs text-[#4A5568] mt-0.5">{row.student_class} · {row.admission_id}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#0F1B47]/5"><XIcon size={16} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Detail label="Status">
              <span className={`text-[10px] font-bold tracking-[0.16em] uppercase rounded-full px-2 py-0.5 border ${STATUS_TONE[row.status] || STATUS_TONE.pending}`}>{row.status}</span>
            </Detail>
            <Detail label="Days">{row.days}</Detail>
            <Detail label="Start date">{fmtDate(row.start_date)}</Detail>
            <Detail label="Submitted">{fmtDateTime(row.created_at)}</Detail>
          </div>
          <Detail label="Requested by">
            <div className="text-sm text-[#0F1B47]">{row.parent_name || "—"}</div>
            <div className="text-xs text-[#4A5568]">{row.parent_email}</div>
          </Detail>
          <Detail label="Reason">
            <div className="text-sm text-[#0F1B47] leading-relaxed">{row.reason}</div>
          </Detail>
          {row.decided_by && (
            <Detail label="Decided by">
              <div className="text-xs text-[#4A5568]">{row.decided_by} on {fmtDateTime(row.updated_at)}</div>
            </Detail>
          )}
          <div>
            <span className="text-xs font-bold tracking-[0.14em] uppercase text-[#0F1B47] mb-2 block">Admin note (visible to parent)</span>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              disabled={!editable}
              maxLength={400}
              rows={3}
              placeholder="Optional note for the parent (e.g. Take care, get well soon)"
              className="w-full bg-[#F5F5F0] border border-[#0F1B47]/12 rounded-2xl px-4 py-2.5 text-[14px] text-[#0F1B47] placeholder:text-[#718096] focus:outline-none focus:border-[#648DE5] focus:bg-[#FDFDF9] disabled:opacity-60 resize-none"
              data-testid="leave-admin-note"
            />
          </div>
        </div>
        {editable && (
          <div className="px-6 py-4 border-t border-[#0F1B47]/8 flex items-center gap-3 sticky bottom-0 bg-[#FDFDF9]">
            <button
              onClick={() => decide("approved")}
              disabled={!!saving}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50"
              data-testid="leave-approve-btn"
            >
              <Check size={14} /> {saving === "approved" ? "Approving…" : "Approve"}
            </button>
            <button
              onClick={() => decide("declined")}
              disabled={!!saving}
              className="inline-flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 rounded-full px-5 py-2.5 font-semibold text-sm hover:bg-red-100 disabled:opacity-50"
              data-testid="leave-decline-btn"
            >
              <XIcon size={14} /> {saving === "declined" ? "Saving…" : "Decline"}
            </button>
            <button onClick={onClose} className="ml-auto text-sm font-semibold text-[#0F1B47] px-4 py-2 rounded-full bg-[#0F1B47]/5 hover:bg-[#0F1B47]/10">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-[#0F1B47]/55 mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}
